/** Cytoscape.js graph visualization wrapper with focus mode and search-highlight */

import { useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react'
import cytoscape from 'cytoscape'
import type { Core, EventObject } from 'cytoscape'
import { CY_STYLE } from './graph-canvas-styles'
import { toElements, applyFocusMode, clearFocusMode, applyHoverHighlight, clearHoverHighlight, applyLoD, showLabelOnHover, hideLabelAfterHover } from './graph-canvas-helpers'
import { GraphCanvasControls, GraphLegend } from './graph-canvas-controls'
import type { GraphPerformanceSample } from './graph-performance-gate'
import type { GraphResponse } from '@agentwiki/shared'

/** Public imperative handle for parent components */
export interface GraphCanvasHandle {
  fitToView: () => void
  highlightSearch: (query: string) => void
  clearHighlight: () => void
  centerOnNode: (nodeId: string) => void
}

interface GraphCanvasProps {
  data: GraphResponse | undefined
  onNodeSelect?: (nodeId: string) => void
  onNodeNavigate?: (nodeId: string) => void
  onPerformanceSample?: (sample: GraphPerformanceSample) => void
  className?: string
}

export const GraphCanvas = forwardRef<GraphCanvasHandle, GraphCanvasProps>(
  function GraphCanvas({ data, onNodeSelect, onNodeNavigate, onPerformanceSample, className = '' }, ref) {
    const containerRef = useRef<HTMLDivElement>(null)
    const cyRef = useRef<Core | null>(null)
    const callbacksRef = useRef({ onNodeSelect, onNodeNavigate })
    const performanceRef = useRef<GraphPerformanceSample | null>(null)
    const interactionSamplesRef = useRef<number[]>([])
    const interactionFrameRef = useRef<number | null>(null)
    callbacksRef.current = { onNodeSelect, onNodeNavigate }

    const publishPerformance = useCallback((sample: GraphPerformanceSample) => {
      performanceRef.current = sample
      onPerformanceSample?.(sample)
    }, [onPerformanceSample])

    const recordInteractionLatency = useCallback(() => {
      if (interactionFrameRef.current !== null) return

      const startedAt = performance.now()
      interactionFrameRef.current = requestAnimationFrame(() => {
        interactionFrameRef.current = null
        const base = performanceRef.current
        if (!base) return

        const latencyMs = Math.round(performance.now() - startedAt)
        interactionSamplesRef.current = [...interactionSamplesRef.current, latencyMs].slice(-40)
        const sorted = [...interactionSamplesRef.current].sort((a, b) => a - b)
        const p95Index = Math.max(0, Math.ceil(sorted.length * 0.95) - 1)
        publishPerformance({ ...base, interactionP95Ms: sorted[p95Index] })
      })
    }, [publishPerformance])

    /** Expose imperative handle to parent */
    useImperativeHandle(ref, () => ({
      fitToView: () => {
        const cy = cyRef.current
        if (!cy) return
        clearFocusMode(cy)
        clearHoverHighlight(cy)
        cy.fit(undefined, 40)
      },
      highlightSearch: (query: string) => {
        const cy = cyRef.current
        if (!cy) return
        if (!query.trim()) { cy.nodes().removeClass('search-match'); return }
        clearFocusMode(cy)
        clearHoverHighlight(cy)
        cy.nodes().removeClass('search-match')
        const lowerQuery = query.toLowerCase()
        cy.nodes().forEach((node) => {
          const label = (node.data('label') ?? '').toLowerCase()
          if (label.includes(lowerQuery)) node.addClass('search-match')
        })
        // Center viewport on matches
        const matches = cy.nodes('.search-match')
        if (matches.length) cy.animate({ fit: { eles: matches, padding: 60 }, duration: 400 })
      },
      clearHighlight: () => {
        cyRef.current?.nodes().removeClass('search-match')
      },
      centerOnNode: (nodeId: string) => {
        const cy = cyRef.current
        if (!cy) return
        const node = cy.getElementById(nodeId)
        if (node.length) cy.animate({ center: { eles: node }, zoom: Math.max(cy.zoom(), 1.5), duration: 400 })
      },
    }), [])

    /** Initialize Cytoscape once */
    useEffect(() => {
      if (!containerRef.current) return

      const cy = cytoscape({
        container: containerRef.current,
        elements: [],
        style: CY_STYLE as never,
        layout: { name: 'preset' },
        minZoom: 0.2,
        maxZoom: 3,
        wheelSensitivity: 0.3,
      })

      // Node tap: select + apply focus mode
      cy.on('tap', 'node', (evt: EventObject) => {
        const nodeId = evt.target.id()
        callbacksRef.current.onNodeSelect?.(nodeId)
        applyFocusMode(cy, nodeId)
        recordInteractionLatency()
      })

      // Double-tap: navigate to document
      cy.on('dbltap', 'node', (evt: EventObject) => {
        callbacksRef.current.onNodeNavigate?.(evt.target.id())
        recordInteractionLatency()
      })

      // Tap on background: clear focus + hover
      cy.on('tap', (evt: EventObject) => {
        if (evt.target === cy) {
          clearFocusMode(cy)
          clearHoverHighlight(cy)
          recordInteractionLatency()
        }
      })

      // Hover: highlight neighborhood + smart labels + tooltip (GH-128)
      cy.on('mouseover', 'node', (evt: EventObject) => {
        const node = evt.target
        applyHoverHighlight(cy, node.id())
        showLabelOnHover(cy, node.id())
        // Tooltip with category info
        const category = node.data('category')
        const summary = node.data('summary')
        const tags = node.data('tags')
        const degree = node.data('degree')
        const parts = [node.data('label')]
        if (category && category !== 'uncategorized') parts.push(`Type: ${category}`)
        if (summary) parts.push(summary)
        if (tags) parts.push(`Tags: ${tags}`)
        parts.push(`Connections: ${degree}`)
        containerRef.current?.setAttribute('title', parts.join('\n'))
      })
      cy.on('mouseout', 'node', (evt: EventObject) => {
        clearHoverHighlight(cy)
        hideLabelAfterHover(cy, evt.target.id())
        containerRef.current?.removeAttribute('title')
      })

      // Level of Detail: adjust visibility based on zoom level (throttled)
      let lodTimer: ReturnType<typeof setTimeout> | null = null
      cy.on('zoom', () => {
        if (lodTimer) return
        lodTimer = setTimeout(() => { lodTimer = null; applyLoD(cy) }, 16)
        recordInteractionLatency()
      })
      cy.on('pan', recordInteractionLatency)

      cyRef.current = cy
      return () => {
        if (interactionFrameRef.current !== null) cancelAnimationFrame(interactionFrameRef.current)
        cy.destroy()
        cyRef.current = null
      }
    }, [recordInteractionLatency])

    /** Update elements when data changes (preserve zoom/pan state) */
    useEffect(() => {
      const cy = cyRef.current
      if (!cy || !data) return

      cy.elements().remove()
      cy.add(toElements(data))

      // Scale layout parameters with graph size for better spacing
      const nodeCount = data.nodes.length
      const edgeCount = data.edges.length
      const repulsion = nodeCount > 80 ? 20000 : nodeCount > 40 ? 16000 : 12000
      const edgeLen = nodeCount > 80 ? 220 : nodeCount > 40 ? 180 : 160
      const startedAt = performance.now()
      const viewport = window.matchMedia('(max-width: 640px)').matches ? 'mobile' : 'desktop'
      interactionSamplesRef.current = []

      cy.layout({
        name: 'cose',
        animate: false,
        nodeRepulsion: () => repulsion,
        idealEdgeLength: () => edgeLen,
        gravity: nodeCount > 40 ? 0.15 : 0.25,
        numIter: 500,
        nodeDimensionsIncludeLabels: true,
        padding: 30,
      } as never).run()
      let frame: number | null = null
      const fitTimer = setTimeout(() => {
        cy.fit(undefined, viewport === 'mobile' ? 130 : 40)
        applyLoD(cy)
        frame = requestAnimationFrame(() => {
          publishPerformance({
            nodeCount,
            edgeCount,
            coldLoadMs: Math.round(performance.now() - startedAt),
            viewport,
          })
        })
      }, 50)

      return () => {
        clearTimeout(fitTimer)
        if (frame !== null) cancelAnimationFrame(frame)
      }
    }, [data, publishPerformance])

    const handleFit = useCallback(() => {
      const cy = cyRef.current
      if (!cy) return
      clearFocusMode(cy)
      clearHoverHighlight(cy)
      cy.fit(undefined, 40)
    }, [])

    const handleZoomIn = useCallback(() => {
      const cy = cyRef.current
      if (cy) cy.zoom({ level: cy.zoom() * 1.3, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } })
    }, [])

    const handleZoomOut = useCallback(() => {
      const cy = cyRef.current
      if (cy) cy.zoom({ level: cy.zoom() / 1.3, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } })
    }, [])

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === '+' || event.key === '=') {
        event.preventDefault()
        handleZoomIn()
      } else if (event.key === '-') {
        event.preventDefault()
        handleZoomOut()
      } else if (event.key === '0') {
        event.preventDefault()
        handleFit()
      }
    }, [handleFit, handleZoomIn, handleZoomOut])

    return (
      <div
        className={`relative flex-1 outline-none focus-visible:ring-1 focus-visible:ring-brand-500/40 ${className}`}
        tabIndex={0}
        role="application"
        aria-label="Interactive knowledge graph"
        onKeyDown={handleKeyDown}
        data-testid="knowledge-graph-canvas"
        data-node-count={data?.nodes.length ?? 0}
        data-edge-count={data?.edges.length ?? 0}
      >
        <div ref={containerRef} className="h-full w-full" />

        <GraphCanvasControls onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} onFit={handleFit} />
        <GraphLegend />
      </div>
    )
  },
)
